import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useDataGrid } from 'hooks/useDataGrid'
import type { Column, DataGridProps } from 'types/data-grid'
import { cn } from 'utils/cn'

import { MobileCardList, MobileFilterInput, MobileSortSelect } from './MobileList'
import { ColumnFilter, HeaderLabel, PaginationButton, SkeletonRows, tableMinWidth } from './shared'

export function DataGrid<T>({
  columns,
  data,
  keyField,
  pageSize = 20,
  emptyMessage = 'No data',
  loading = false,
  onRowClick,
  storageKey,
}: DataGridProps<T>) {
  const { rows, sort, filters, page, totalPages, totalRows, toggleSort, setSort, updateFilter, clearFilter, setPage } =
    useDataGrid(data, pageSize, { storageKey, columns })

  const hasFilters = columns.some((c) => c.filterable)
  const hasMobileControls = hasFilters || columns.some((c) => c.sortable)
  const cell = (col: Column<T>, row: T) =>
    col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '')

  return (
    <div className="flex flex-col gap-3">
      {hasMobileControls && (
        <div className="flex items-center gap-2 md:hidden">
          <MobileFilterInput
            columns={columns}
            filters={filters}
            updateFilter={updateFilter}
            clearFilter={clearFilter}
          />
          <MobileSortSelect columns={columns} sort={sort} setSort={setSort} />
        </div>
      )}
      <div className="overflow-hidden rounded-lg border border-border/50 text-sm text-foreground shadow-md shadow-black/5 dark:border-transparent dark:shadow-black/30">
        <div className="md:hidden">
          <MobileCardList
            columns={columns}
            rows={rows}
            keyField={keyField}
            cell={cell}
            onRowClick={onRowClick}
            loading={loading}
            emptyMessage={emptyMessage}
          />
        </div>
        <div className="hidden md:block">
          <Table className="table-fixed" style={{ minWidth: tableMinWidth(columns) }}>
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} style={col.width ? { width: col.width } : undefined} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    className={cn(
                      'overflow-hidden whitespace-nowrap bg-muted/30 px-3.5 py-2.5 text-[13px] font-semibold',
                      col.sortable && 'cursor-pointer select-none',
                    )}
                    style={{ textAlign: (col as Column<unknown>).align ?? 'left' }}>
                    <HeaderLabel header={col.header} column={col.key} sortable={col.sortable} sort={sort} />
                  </TableHead>
                ))}
              </TableRow>
              {hasFilters && (
                <TableRow className="hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead key={col.key} className="overflow-hidden bg-muted/30 px-3.5 py-1 font-normal">
                      {col.filterable ? (
                        <ColumnFilter
                          header={col.header}
                          filter={filters[col.key]}
                          onUpdate={(op, val) => updateFilter(col.key, op, val)}
                          onClear={() => clearFilter(col.key)}
                        />
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              <LoadingWrapper loading={loading} loadingElement={<SkeletonRows columns={columns} />}>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={String((row as Record<string, unknown>)[keyField])}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={
                        onRowClick
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onRowClick(row)
                              }
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      className={onRowClick ? 'cursor-pointer' : undefined}>
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          className="truncate px-3.5 py-2.5"
                          style={{ textAlign: (col as Column<unknown>).align ?? 'left' }}>
                          {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </LoadingWrapper>
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border/50 bg-muted/30 px-3.5 py-2.5 text-[13px] text-muted-foreground">
            <span>
              {totalRows} {totalRows === 1 ? 'row' : 'rows'}
            </span>
            <div className="flex items-center gap-1.5">
              <PaginationButton onClick={() => setPage(0)} disabled={page === 0}>
                {'\u00AB'}
              </PaginationButton>
              <PaginationButton onClick={() => setPage(page - 1)} disabled={page === 0}>
                {'\u2039'}
              </PaginationButton>
              <span className="px-2">
                {page + 1} / {totalPages}
              </span>
              <PaginationButton onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                {'\u203A'}
              </PaginationButton>
              <PaginationButton onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
                {'\u00BB'}
              </PaginationButton>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
